import { useState, useEffect } from 'react';

import {
  IconTrash,
  IconAlertCircle,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import ConfirmationModal from '@/components/ConfirmationModal';
import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import ProgressBar from '@/components/ProgressBar';
import LessonDetailsSkeleton from '@/components/SkeletonLoader/LessonDetailsSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { deleteGame, getLessonDetails } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { ILessonDetails } from '@/types';
import { getStatusLabel, getStatusColorClass } from '@/utils';

const LessonDetailsModal = ({
  setIsOpen,
  lesson_id,
}: {
  setIsOpen: (open: boolean) => void;
  lesson_id: string;
}) => {
  const { hasAccess } = useAccess();

  const { loadingModal, setLoadingModal } = useCommonStore();
  const { formatDate } = useDateFormatter();
  const { formatText } = useFormatText();
  const [lessonDetails, setLessonDetails] = useState<ILessonDetails | null>(
    null
  );
  const [selectedGame, setSelectedGame] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isRemoveGameModal, setIsRemoveGameModal] = useState(false);

  useEffect(() => {
    if (!lesson_id) return;
    fetchLessonDetails();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerContent = (
    <div className="flex justify-between items-center w-full mr-4">
      <p className="text-neutral-primary font-medium leading-7 text-lg">
        {formatText('user-management.lesson.details')}
      </p>
      {/* <IconShare
        size={24}
        className="text-neutral-pressed w-8 h-8 p-1.5 rounded-sm bg-neutral-light-50"
      /> */}
    </div>
  );

  const fetchLessonDetails = async () => {
    setLoadingModal(true);
    try {
      const response = await getLessonDetails(lesson_id);

      if (response?.status) {
        const data = response?.data;
        setLessonDetails(data);
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleRemoveGame = async () => {
    if (!selectedGame) return;
    setLoadingModal(true);

    try {
      const response = await deleteGame(lesson_id, selectedGame.id);
      if (response?.status) {
        fetchLessonDetails();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setIsRemoveGameModal(false);
      setSelectedGame(null);
    }
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <>
      <CustomModal
        setIsOpen={setIsOpen}
        isHeader
        position="right"
        headercontent={headerContent}
        loadingModal={loadingModal}
      >
        {loadingModal ? (
          <LessonDetailsSkeleton />
        ) : (
          <div className="space-y-10 w-full bg-white">
            {/* Overview */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('user-management.lesson.overview')}
              </h3>

              <div className="bg-brand-light-50 rounded-2xl p-6 space-y-4">
                {/* Title */}
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <IconDeviceGamepad2
                      size={32}
                      className=" text-white p-2 rounded-lg bg-brand-default shrink-0"
                    />

                    <h3 className="text-lg font-semibold text-neutral-primary">
                      {lessonDetails?.title}
                    </h3>
                  </div>

                  {/* <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColorClass(lesson.status)}`}
              >
                {lesson.status}
              </span> */}
                </div>

                {/* Description */}
                {lessonDetails?.description && (
                  <p className="text-neutral-secondary">
                    {lessonDetails?.description}
                  </p>
                )}

                {/* Dates */}
                <div className="space-y-4">
                  <div className="flex justify-between text-base">
                    <span className="text-neutral-secondary">
                      {formatText('user-management.lesson.assignedDate')}
                    </span>
                    <span className="font-semibold text-neutral-primary">
                      {lessonDetails?.assign_date
                        ? formatDate(lessonDetails?.assign_date)
                        : '-'}
                    </span>
                  </div>

                  <div className="flex justify-between text-base">
                    <span className="text-neutral-secondary">
                      {formatText('user-management.lesson.dueDate')}
                    </span>
                    <span className="font-semibold text-neutral-primary">
                      {lessonDetails?.due_date
                        ? formatDate(lessonDetails?.due_date)
                        : '-'}
                    </span>
                  </div>
                  {/* Progress */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-neutral-secondary">
                        {formatText('user-management.lesson.column.progress')}
                      </span>
                      <span className="font-semibold text-neutral-primary">
                        {lessonDetails?.progress ?? 0}%
                      </span>
                    </div>

                    <ProgressBar
                      value={lessonDetails?.progress ?? 0}
                      showPercentage={false}
                      barColor="bg-brand-default"
                      backgroundColor="bg-neutral-light-50"
                      height="h-2"
                    />
                  </div>
                </div>
              </div>

              {/* Instructions */}
              {lessonDetails?.instructions && (
                <div className="rounded-2xl bg-yellow-50 p-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <IconAlertCircle
                      size={32}
                      className="text-white p-2 rounded-lg bg-yellow-500 shrink-0"
                    />
                    <h4 className="font-semibold text-neutral-primary">
                      {formatText('user-management.lesson.instructions')}
                    </h4>
                  </div>
                  <p className="text-sm text-neutral-secondary">
                    {lessonDetails?.instructions}
                  </p>
                </div>
              )}

              {/* Stats */}
              <div className="grid sm:grid-cols-3 gap-6">
                <SummaryCard
                  label={formatText('user-management.lesson.avgTime')}
                  value={lessonDetails?.avg_time ?? 0}
                  bg="bg-success-light-50"
                  text="text-green-900"
                  sub="text-success-secondary"
                  variant="highlight"
                  isLoading={loadingModal}
                />
                <SummaryCard
                  label={formatText('user-management.lesson.accuracy')}
                  value={`${lessonDetails?.accuracy ?? 0}%`}
                  bg="bg-indigo-100"
                  text="text-indigo-900"
                  sub="text-indigo-600"
                  variant="highlight"
                  isLoading={loadingModal}
                />
                <SummaryCard
                  label={formatText('user-management.lesson.games')}
                  value={lessonDetails?.games ?? 0}
                  bg="bg-warning-light-100"
                  text="text-warning-primary"
                  sub="text-warning-secondary"
                  variant="highlight"
                  isLoading={loadingModal}
                />
              </div>
            </div>

            {/* Lesson Breakdown */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-neutral-primary">
                  {formatText('user-management.lesson.lesson-breakdown')}
                </h3>
                {lessonDetails?.lesson_breakdown &&
                  lessonDetails?.lesson_breakdown?.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-neutral-light-100 text-xs font-semibold">
                      {lessonDetails?.lesson_breakdown?.length
                        ?.toString()
                        ?.padStart(2, '0')}
                    </span>
                  )}
              </div>

              <div className="space-y-4">
                {lessonDetails?.lesson_breakdown &&
                lessonDetails?.lesson_breakdown?.length > 0 ? (
                  lessonDetails?.lesson_breakdown?.map((item) => (
                    <div
                      key={item?.id}
                      className="p-4 rounded-2xl border border-neutral-light-200 flex justify-between items-center"
                    >
                      <div className="flex flex-col gap-0.5 max-w-[70%]">
                        <div className="flex items-center gap-2 ">
                          <span
                            className="ftext-base font-semibold text-neutral-primary truncate"
                            title={item?.title}
                          >
                            {item?.title}
                          </span>
                          {item?.quarters !== null &&
                            item?.quarters !== undefined && (
                              <span
                                className={
                                  'font-semibold text-xs py-1 px-3 rounded-full inline-block whitespace-nowrap bg-purple-50 text-brand-default'
                                }
                              >
                                {`Q${item.quarters}`}
                              </span>
                            )}
                          {item?.rounds !== null &&
                            item?.rounds !== undefined && (
                              <span
                                className={
                                  'font-semibold text-xs py-1 px-3 rounded-full inline-block whitespace-nowrap bg-neutral-100 text-neutral-primary'
                                }
                              >
                                {`R${item.rounds}`}
                              </span>
                            )}
                        </div>
                        <div className="text-sm text-neutral-secondary mt-1">
                          {item?.completed_students}/{item?.total_student}{' '}
                          {formatText('user-management.lesson.completed')}
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColorClass(item.status)}`}
                        >
                          {getStatusLabel(item.status)}
                        </span>
                        <IconTrash
                          size={24}
                          strokeWidth={1.5}
                          className="text-red-500 cursor-pointer"
                          onClick={() => {
                            setSelectedGame({
                              id: item.id,
                              title: item.title,
                            });
                            setIsRemoveGameModal(true);
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center flex-col gap-3 justify-center w-full px-0 pt-10">
                    <CustomImage
                      src={'/assets/images/lesson-breakdown-empty.png'}
                      alt="noData"
                      width={100}
                      height={100}
                    />
                    <p className="text-neutral-900 text-xl font-semibold sm:text-nowrap ">
                      {formatText('common.no-data-found')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CustomModal>

      {isRemoveGameModal && (
        <ConfirmationModal
          isOpen={isRemoveGameModal}
          setIsOpen={setIsRemoveGameModal}
          title="user-management.lesson.remove-game-title"
          descriptionPrefix="user-management.lesson.remove-game-prefix"
          descriptionSuffix="user-management.lesson.remove-game-suffix"
          entityName={selectedGame?.title || ''}
          confirmLabel={formatText('user-management.lesson.remove-game')}
          onConfirm={handleRemoveGame}
          isLoading={loadingModal}
          isDestructive
        />
      )}
    </>
  );
};

export default LessonDetailsModal;
